// The Tysiąc table (.ai/tysiac.md): 3-5-8's table for 3 seats (358/Table358.tsx; the root keeps its `.t58` class, so its
// layout, musik, badge and panel styles apply, with `.t1k` for what differs). On a computer the classic table (the 2
// computer players' backs left and right, the trick as a "T", the musik above it, the hand fanned at the bottom); on a
// phone the compact one. The bidding, the redeal question and the contract are panels over the field; the musik turns
// up for all and flies to the declarer; each card he gives flies to its taker. The end of a deal opens the score window.
// Tap or click only; the root keeps the `.ht` class, so ui/tapClick.ts clicks its taps on the finger's lift (iPad).
import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { t } from '../i18n';
import { QuickSay, SayBubble } from '../online/QuickSay';
import { NextDealNote } from '../online/NextDealNote';
import { SeatAvatar, seatFlag as seatFlagOf } from '../online/SeatAvatar';
import { flagged } from '../online/PlayerName';
import { SeatWait, TurnClock } from '../online/TurnClock';
import { mpStore, useMultiplayerState } from '../multiplayer/store';
import { backSrc, backSrcSet, faceSrc, faceSrcSet } from '../options/assets';
import { uiStore, useUiState, type WindowInstance } from '../store/uiStore';
import { useController } from '../store/gameStore';
import { isShortGame, reloadForNewGame } from '../hearts/nextGame';
import { AdSlot } from '../windows/AdSlot';
import { WindowFrame } from '../windows/WindowFrame';
import { useFitWindow } from '../windows/useFitWindow';
import { CARD_RATIO, Face, SHADOW, backPic, centre, facePic, flightShadow, ghost, usePhone, whenLanded } from '../hearts/HeartsTable';
import { CrownIcon, LeadIcon, withSuits } from '../hearts/suits';
import { fixedNames } from '../hearts/names';
import {
  MARRIAGE, barrelOf, bidOptions, cardKey, contractOptions, giveTo, leaders, legalCards, meldPoints, meldsWith, playerWon, sameCard,
  winScore, type Played, type State1000,
} from './rules';
import { COLLECT_MS, DEAL_FLIGHT_MS, DEAL_STEP_MS, GIVE_FLY_MS, MUSIK_FLY_MS, store1000, use1000, type TableState } from './store';
import '../hearts/hearts.css';
import '../makao/makao.css';
import '../358/t58.css';
import './t1k.css';

/** A sentence with each real player's flag beside the name (owner 2026-11-01); not the hint over the hand, which has the
 *  suits only (owner 2026-11-02: "usuń stąd flagę użytkownika"). */
const flaggedText = (text: string) => flagged(text, store1000().getState().names);

const SYM = { C: '♣', D: '♦', S: '♠', H: '♥' } as const;
const FLY_MS = 340;
const CLOSE_MS = 450;

export const seatName = (seat: number): string => (seat === 0 ? t('hearts.you') : (store1000().getState().names ?? fixedNames(t)).name[seat]!);
/** Online: the seat's flag badge (a real player), else null (online/SeatAvatar.tsx). */
const seatFlag = (seat: number): string | null => seatFlagOf(store1000().getState().names, seat);

/** New game after a finished game: a full reload (the ads refresh); after a game under 2 minutes the next one at once
 *  (hearts/nextGame.ts). */
function useNewGame(): () => void {
  const ctrl = useController();
  const { startedAt, endedAt } = use1000();
  return () => (isShortGame(startedAt, endedAt) ? void ctrl.newGame() : reloadForNewGame('thousand'));
}

/** Where the player's cards and the musik's were on screen at the last render: a card that left flies from there. */
const handRects = new Map<string, DOMRect>();
const musikRects: DOMRect[] = [];
const seatBox = (seat: number): DOMRect | null => document.querySelector(`.t1k [data-src="${seat}"]`)?.getBoundingClientRect() ?? null;
const musikBox = (): DOMRect | null => document.querySelector('.t1k .t58-musik')?.getBoundingClientRect() ?? null;
const box = (r: DOMRect, w: number) => {
  const c = centre(r), h = w * CARD_RATIO;
  return new DOMRect(c.x - w / 2, c.y - h / 2, w, h);
};
const cardW = () => document.querySelector('.t1k .ht-hand .ht-card')?.getBoundingClientRect().width ?? 70;

/** What a tap on a card of the hand does now (null: nothing). */
function tapMode(g: State1000): 'give' | 'play' | null {
  if (g.phase === 'give' && g.declarer === 0) return 'give';
  if (g.phase === 'play' && g.turn === 0) return 'play';
  return null;
}

/** The player's hand; the cards not allowed dim. */
function Hand({ g, deck }: { g: State1000; deck: string }) {
  const store = store1000();
  const mode = tapMode(g);
  const legal = mode === 'play' ? legalCards(g, 0) : null;
  const ref = useRef<HTMLDivElement>(null);
  const placed = useRef(new Map<string, { x: number; y: number }>());
  useLayoutEffect(() => {
    const hand = ref.current;
    if (!hand) return;
    const now = new Map<string, { x: number; y: number }>();
    for (const el of hand.querySelectorAll<HTMLElement>('.ht-card')) {
      now.set(el.dataset.card!, { x: el.offsetLeft, y: el.offsetTop });
      handRects.set(el.dataset.card!, el.getBoundingClientRect());
    }
    const before = placed.current;
    placed.current = now;
    if (!before.size || typeof hand.animate !== 'function' || hand.closest('[data-dealing]')) return;
    for (const el of hand.querySelectorAll<HTMLElement>('.ht-card')) {
      const was = before.get(el.dataset.card!), is = now.get(el.dataset.card!)!;
      if (!was || (Math.abs(was.x - is.x) < 0.5 && Math.abs(was.y - is.y) < 0.5)) continue;
      el.animate([{ transform: `translate(${was.x - is.x}px, ${was.y - is.y}px)` }, { transform: 'none' }], { duration: CLOSE_MS, easing: 'ease-in-out' });
    }
  });
  const cards = g.hands[0]!;
  const n = cards.length;
  return (
    <div ref={ref} className="ht-hand" data-count={n} style={{ '--n': n, '--step': Math.min(0.62, 7.2 / Math.max(1, n - 1)), '--cols': n > 8 ? 10 : 8 } as CSSProperties}>
      {cards.map((c) => {
        const can = mode !== null && (legal === null || legal.some((x) => sameCard(x, c)));
        const meld = mode === 'play' && meldsWith(g, 0, c);
        return (
          <button
            key={cardKey(c)}
            type="button"
            className="ht-card"
            data-card={cardKey(c)}
            data-meld={meld || undefined}
            data-off={mode !== null && !can ? '' : undefined}
            onClick={(e) => {
              if (!can) return;
              handRects.set(cardKey(c), e.currentTarget.getBoundingClientRect());
              store.tap(c);
            }}
          >
            <span className="ht-sh" aria-hidden="true" />
            <Face card={c} deck={deck} />
          </button>
        );
      })}
    </div>
  );
}

/** A card of the trick: it flies in from its player (FLIP). */
function TrickCard({ p, g, deck, className }: { p: Played; g: State1000; deck: string; className: string }) {
  const fly = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = fly.current;
    if (!el || typeof el.animate !== 'function') return;
    const to = el.getBoundingClientRect();
    const from = p.seat === 0 ? (handRects.get(cardKey(p.card)) ?? seatBox(0)) : seatBox(p.seat);
    if (!from || !to.width) return;
    const a = centre(from), b = centre(to);
    const scale = p.seat === 0 ? from.width / to.width : 0.6;
    el.animate([{ transform: `translate(${a.x - b.x}px, ${a.y - b.y}px) scale(${scale})` }, { transform: 'none' }], { duration: FLY_MS, easing: 'cubic-bezier(.2,.75,.3,1)' });
    flightShadow(el, SHADOW.arc, { duration: FLY_MS });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- once, when the card arrives
  return (
    <div className={`ht-trick__slot ${className}`} data-seat={p.seat} data-card={cardKey(p.card)} data-winner={g.phase === 'trick-done' && g.winner === p.seat ? '' : undefined}>
      <div ref={fly} className="ht-trick__fly">
        <span className="ht-sh" aria-hidden="true" />
        <Face card={p.card} deck={deck} />
      </div>
    </div>
  );
}

/** The musik's place above the trick: face down during the bidding, face up for everyone once it is won, empty once
 *  taken. */
function Musik({ g, deck, back }: { g: State1000; deck: string; back: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const cards = ref.current?.querySelectorAll('.t58-musik__card');
    if (!cards?.length) return;
    musikRects.length = 0;
    cards.forEach((el) => musikRects.push(el.getBoundingClientRect()));
  });
  const up = g.phase === 'musik';
  return (
    <div ref={ref} className="t58-musik" data-up={up || undefined} aria-label={t('t58.musik')}>
      {g.musik.map((c, i) =>
        up ? (
          <div key={cardKey(c)} className="t58-musik__card" data-card={cardKey(c)}>
            <Face card={c} deck={deck} />
          </div>
        ) : (
          <div key={`m${i}`} className="t58-musik__card">
            <img className="ht-face" src={backSrc(back)} srcSet={backSrcSet(back)} alt="" draggable={false} />
          </div>
        ),
      )}
    </div>
  );
}

/** The badge on the field once the bidding is over: who plays how much, and the trumps (none until a marriage). */
function ContractBadge({ g }: { g: State1000 }) {
  // not while the musik lies face up: on a low screen the badge stands where the musik is (the hint and the plate say
  // who plays and the bid)
  if (g.declarer < 0 || g.phase === 'musik') return null;
  const n = g.phase === 'give' ? g.bid : g.contract;
  const trump = g.phase === 'play' || g.phase === 'trick-done' ? (g.trump ? t('t58.contract.trump', { suit: SYM[g.trump] }) : t('t58.contract.NT')) : '';
  return (
    <div className="t58-contract">
      <span className="mk-badge">{flaggedText(`${t('t1k.badge', { name: seatName(g.declarer), n })}${trump ? ` · ${trump}` : ''}`)}</span>
    </div>
  );
}

/** − 120 +: a value from `options`. */
function Stepper({ options, value, onChange }: { options: readonly number[]; value: number; onChange: (v: number) => void }) {
  const i = Math.max(0, options.indexOf(value));
  return (
    <div className="mk-pick__row t1k-stepper">
      <button type="button" className="mk-pick__btn" data-step="down" disabled={i === 0} onClick={() => onChange(options[i - 1]!)} aria-label="−">
        −
      </button>
      <output className="t1k-stepper__value">{options[i]}</output>
      <button type="button" className="mk-pick__btn" data-step="up" disabled={i >= options.length - 1} onClick={() => onChange(options[i + 1]!)} aria-label="+">
        +
      </button>
    </div>
  );
}

/** The player's panels over the field: the redeal question, the bidding, the contract (keyed by the deal, the phase and
 *  the bid: the stepper starts again at the lowest value). */
function Panel({ g }: { g: State1000 }) {
  const store = store1000();
  const bids = g.phase === 'bid' && g.turn === 0 ? bidOptions(g, 0) : [];
  const contracts = g.phase === 'contract' && g.declarer === 0 ? contractOptions(g) : [];
  const [pick, setPick] = useState<number | null>(null);
  if (g.phase === 'redeal' && g.asking[0] === 0)
    return (
      <div className="mk-pick t58-pick t1k-pick" role="dialog" aria-label={t('t1k.redeal.title')} data-panel="redeal">
        <b>{t('t1k.redeal.title')}</b>
        <small>{t('t1k.redeal.note')}</small>
        <div className="mk-pick__row">
          <button type="button" className="mk-pick__btn mk-pick__btn--wide" id="t1k_redeal" onClick={() => store.redeal(true)}>
            {t('t1k.redeal.yes')}
          </button>
          <button type="button" className="mk-pick__btn mk-pick__btn--wide" id="t1k_keep" onClick={() => store.redeal(false)}>
            {t('t1k.redeal.no')}
          </button>
        </div>
      </div>
    );
  if (g.phase === 'bid' && g.turn === 0) {
    const value = pick ?? bids[0] ?? g.bid;
    return (
      <div className="mk-pick t58-pick t1k-pick" role="dialog" aria-label={t('t1k.bid.title')} data-panel="bid">
        <b>{t('t1k.bid.title')}</b>
        <small>{t('t1k.bid.high', { n: g.bid, name: seatName(g.bidder) })}</small>
        {bids.length ? <Stepper options={bids} value={value} onChange={setPick} /> : null}
        <div className="mk-pick__row">
          {bids.length ? (
            <button type="button" className="mk-pick__btn mk-pick__btn--wide" id="t1k_bid" onClick={() => store.bid(value)}>
              {t('t1k.bid.button', { n: value })}
            </button>
          ) : null}
          <button type="button" className="mk-pick__btn mk-pick__btn--wide" id="t1k_pass" onClick={() => store.bid('pass')}>
            {t('t1k.pass')}
          </button>
        </div>
        <small>{bids.length ? t('t1k.bid.note') : t('t1k.bid.cap')}</small>
      </div>
    );
  }
  if (contracts.length) {
    const value = pick ?? contracts[0]!;
    return (
      <div className="mk-pick t58-pick t1k-pick" role="dialog" aria-label={t('t1k.contract.title')} data-panel="contract">
        <b>{t('t1k.contract.title')}</b>
        <Stepper options={contracts} value={value} onChange={setPick} />
        <div className="mk-pick__row">
          <button type="button" className="mk-pick__btn mk-pick__btn--wide" id="t1k_play" onClick={() => store.contract(value)}>
            {t('t1k.contract.button', { n: value })}
          </button>
        </div>
        <small>{t('t1k.contract.note', { n: g.bid })}</small>
      </div>
    );
  }
  return null;
}

/** The last marriage, while its trick is on the table. */
const freshMeld = (g: State1000) => {
  const m = g.melds[g.melds.length - 1];
  return m && m.trick === g.gone.length / 3 && g.trick.length ? m : null;
};

function hint(g: State1000): string {
  if (g.phase === 'redeal') return g.asking[0] === 0 ? t('t1k.hint.redeal') : t('t1k.hint.redealing', { name: seatName(g.asking[0]!) });
  if (g.phase === 'bid') return g.turn === 0 ? t('t1k.hint.bid') : t('t1k.hint.bidding', { name: seatName(g.turn) });
  if (g.phase === 'musik') return t('t58.hint.musik', { name: seatName(g.declarer) });
  if (g.phase === 'give') return g.declarer === 0 ? t('t1k.hint.give', { name: seatName(giveTo(g)) }) : t('t1k.hint.giving', { name: seatName(g.declarer) });
  if (g.phase === 'contract') return g.declarer === 0 ? t('t1k.hint.contract') : t('t1k.hint.contracting', { name: seatName(g.declarer) });
  const meld = freshMeld(g);
  if (meld && g.phase === 'play' && g.turn !== 0) return t('t1k.hint.meld', { name: seatName(meld.seat), suit: SYM[meld.suit], n: MARRIAGE[meld.suit] });
  if (g.phase === 'trick-done') return meld ? t('t1k.hint.meld', { name: seatName(meld.seat), suit: SYM[meld.suit], n: MARRIAGE[meld.suit] }) : t('hearts.hint.trick', { name: seatName(g.winner) });
  if (g.phase !== 'play') return '';
  if (g.turn !== 0) return t('hearts.hint.turn', { name: seatName(g.turn) });
  if (!g.trick.length) return g.hands[0]!.some((c) => meldsWith(g, 0, c)) ? t('t1k.hint.leadMeld') : t('t58.hint.lead');
  const led = g.trick[0]!.card.s;
  if (g.hands[0]!.some((c) => c.s === led)) return t('t1k.hint.follow', { suit: SYM[led] });
  return g.trump && g.hands[0]!.some((c) => c.s === g.trump) ? t('t58.hint.trump', { suit: SYM[led], trump: SYM[g.trump] }) : t('hearts.hint.void', { suit: SYM[led] });
}

/** Online: the seconds left of my move. */
function MyClock() {
  return <TurnClock at={use1000().deadlineAt} />;
}

/** The button by the hint: the next deal / a new game with the window closed (offline only: online the next deal comes
 *  by itself and the score window holds the choices after the game). */
function Actions({ g }: { g: State1000 }) {
  const store = store1000();
  const newGame = useNewGame();
  const online = !!useMultiplayerState().cards;
  if (online) return null;
  if (g.phase === 'hand-end' || g.phase === 'over') {
    if (uiStore().isOpen('t1k_score')) return null;
    return (
      <button type="button" className="ht-go" id="t1k_again" onClick={() => (g.phase === 'over' ? newGame() : store.nextHand())}>
        {t(g.phase === 'over' ? 'hearts.score.newGame' : 'hearts.score.next')}
      </button>
    );
  }
  return null;
}

const turnOf = (g: State1000, seat: number) => (g.phase === 'play' || g.phase === 'bid') && g.turn === seat;

/** What a seat has in this deal: its word in the bidding, the declarer's bid, the points taken in the play. */
function dealLine(g: State1000, seat: number): string {
  if (g.phase === 'redeal' || g.phase === 'bid') {
    const b = g.bids[seat];
    return b === 'pass' ? t('t1k.pass') : typeof b === 'number' ? t('t1k.bidWord', { n: b }) : '';
  }
  if (g.phase === 'musik' || g.phase === 'give' || g.phase === 'contract') return seat === g.declarer ? t('t1k.plays', { n: g.bid }) : '';
  const got = g.taken[seat]! + meldPoints(g, seat);
  if (seat === g.declarer) return t('t1k.takenOf', { n: got, of: g.contract });
  return g.scores[seat]! >= barrelOf(g.target) ? t('t1k.barrel') : t('t1k.taken', { n: got });
}

function Plate({ g, seat, src }: { g: State1000; seat: number; src?: boolean }) {
  const line = dealLine(g, seat);
  return (
    <div className="ht-plate" data-seat={seat} data-turn={turnOf(g, seat) || undefined} data-src={src ? seat : undefined} data-declarer={seat === g.declarer || undefined}>
      <SeatAvatar name={seatName(seat)} flag={seatFlag(seat)} />
      <SeatWait seat={seat} />
      <div className="ht-plate__text">
        <b>{seatName(seat)}</b>
        <span>
          {t('t58.points', { n: g.scores[seat]! })}
          {line ? ` · ${line}` : ''}
        </span>
      </div>
    </div>
  );
}

function Backs({ g, seat, back }: { g: State1000; seat: number; back: string }) {
  return (
    <div className="ht-backs" data-src={seat} aria-hidden="true">
      {g.hands[seat]!.map((_, i) => (
        <img key={i} className="ht-back" src={backSrc(back)} srcSet={backSrcSet(back)} alt="" draggable={false} />
      ))}
    </div>
  );
}

/** A: the classic table (a computer). */
function ClassicTable({ g, deck, back }: { g: State1000; deck: string; back: string }) {
  return (
    <div className="ht-a t58-a">
      <div className="ht-seat ht-seat--left">
        <Plate g={g} seat={1} />
        <Backs g={g} seat={1} back={back} />
      </div>
      <div className="ht-seat ht-seat--right">
        <Plate g={g} seat={2} />
        <Backs g={g} seat={2} back={back} />
      </div>
      <div className="ht-mid">
        <div className="ht-felt" aria-hidden="true" />
        <Musik g={g} deck={deck} back={back} />
        <ContractBadge g={g} />
        <div className="ht-trick ht-trick--cross">
          {g.trick.map((p) => (
            <TrickCard key={`${g.handNo}-${cardKey(p.card)}`} p={p} g={g} deck={deck} className={`ht-trick__slot--s${p.seat === 2 ? 3 : p.seat}`} />
          ))}
        </div>
        <Panel key={`${g.handNo}-${g.phase}-${g.bid}`} g={g} />
      </div>
      <div className="ht-hintrow">
        <span className="ht-hint" data-hint="">
          {withSuits(hint(g))}
          <MyClock />
        </span>
        <Actions g={g} />
      </div>
      <div className="ht-me">
        <Plate g={g} seat={0} src />
      </div>
      <Hand g={g} deck={deck} />
    </div>
  );
}

/** C: compact, big cards (a phone). */
function CompactTable({ g, deck, back }: { g: State1000; deck: string; back: string }) {
  const playing = g.phase === 'play' || g.phase === 'trick-done';
  // the names over the trick keep their order while the bidding goes round (the opener, then the declarer, first)
  const start = g.trick.length ? g.trick[0]!.seat : playing && g.turn >= 0 ? g.turn : g.declarer >= 0 ? g.declarer : g.opener;
  const order = [0, 1, 2].map((k) => (start + k) % 3);
  const line0 = dealLine(g, 0);
  return (
    <div className="ht-c t58-c">
      <div className="ht-chips">
        {[1, 2].map((p) => {
          const line = dealLine(g, p);
          return (
            <div key={p} className="ht-chip" data-seat={p} data-turn={turnOf(g, p) || undefined} data-src={p} data-declarer={p === g.declarer || undefined}>
              <SeatAvatar name={seatName(p)} flag={seatFlag(p)} />
              <SeatWait seat={p} />
              <b>{seatName(p)}</b>
              <span>{t('t58.points', { n: g.scores[p]! })}</span>
              <small>{line || ' '}</small>
            </div>
          );
        })}
      </div>
      <div className="t58-c-mid">
        <div className="t58-c-top">
          <Musik g={g} deck={deck} back={back} />
          <ContractBadge g={g} />
        </div>
        <div className="ht-trick ht-trick--row">
          {order.map((p, k) => {
            const played = g.trick.find((x) => x.seat === p);
            return (
              <div key={p} className="ht-place" data-lead={playing && k === 0 ? '' : undefined}>
                <span>
                  {/* a phone: the name alone over its place, no flag (owner 2026-11-02: "na mobilkach flagi nie renderuj, aby zwolnić miejsce") */}
                  {seatName(p)}
                  {playing && k === 0 ? <LeadIcon /> : null}
                </span>
                {played ? <TrickCard key={`${g.handNo}-${cardKey(played.card)}`} p={played} g={g} deck={deck} className="" /> : <div className="ht-place__empty" />}
              </div>
            );
          })}
        </div>
        <Panel key={`${g.handNo}-${g.phase}-${g.bid}`} g={g} />
      </div>
      {/* a slot of two lines: a longer or shorter hint no longer moves the table above (owner 2026-11-02) */}
      <div className="ht-msgslot">
        <div className="ht-msg">
          <span className="ht-hint" data-hint="">
            {withSuits(hint(g))}
            <MyClock />
          </span>
          <Actions g={g} />
        </div>
      </div>
      <div className="ht-mechip" data-seat={0} data-turn={turnOf(g, 0) || undefined} data-src={0} data-declarer={g.declarer === 0 || undefined}>
        {seatName(0)} · {t('t58.points', { n: g.scores[0]! })}
        {line0 ? ` · ${line0}` : ''}
      </div>
      <Hand g={g} deck={deck} />
    </div>
  );
}

/** The deal: a face-down deck in the middle; 24 cards thrown one after the other round the table from the opener,
 *  3 of them to the musik (never the first or the last, never two in a row). The player's cards fly themselves; the
 *  computer players' backs and the musik as copies above the table. */
const MUSIK_AT = [5, 12, 19];
function useDealAnimation(deal: number, g: State1000 | null, back: string): void {
  useLayoutEffect(() => {
    const root = document.querySelector<HTMLElement>('.t1k');
    const mid = document.querySelector<HTMLElement>('.t1k .ht-felt, .t1k .ht-trick--row');
    if (!deal || !g || !root || !mid || typeof root.animate !== 'function') return;
    const from = centre(mid.getBoundingClientRect());
    const ghosts: HTMLElement[] = [];
    const flights: Animation[] = [];
    const deckW = cardW() * 0.9, deckH = deckW * CARD_RATIO;
    const deckBox = new DOMRect(from.x - deckW / 2, from.y - deckH / 2, deckW, deckH);
    const deckEl = ghost(deckBox, backPic(back), ghosts);
    deckEl.style.zIndex = '40';
    const deckGone = deckEl.animate([{ opacity: 1 }, { opacity: 1, offset: 0.97 }, { opacity: 0 }], { duration: 23 * DEAL_STEP_MS + 60, fill: 'forwards' });
    void deckGone.finished.then(() => deckEl.remove(), () => deckEl.remove());
    flights.push(deckGone);
    // the order of the throws: seat by seat from the opener, the musik's 3 at their steps
    const steps: Array<{ seat: number | 'm'; i: number }> = [];
    const count = [0, 0, 0];
    let m = 0;
    for (let k = 0; k < 24; k++) {
      if (MUSIK_AT.includes(k)) steps.push({ seat: 'm', i: m++ });
      else {
        const seat = (g.opener + (k - m)) % 3;
        steps.push({ seat, i: count[seat]!++ });
      }
    }
    const at = (seat: number | 'm', i: number) => steps.findIndex((s) => s.seat === seat && s.i === i);
    const throwTo = (to: DOMRect, order: number, vanish: boolean) => {
      const c = centre(to);
      const el = ghost(new DOMRect(c.x - deckW / 2, c.y - deckH / 2, deckW, deckH), backPic(back), ghosts);
      const timing = { duration: DEAL_FLIGHT_MS, delay: order * DEAL_STEP_MS };
      const f = el.animate([{ transform: `translate(${from.x - c.x}px, ${from.y - c.y}px)`, opacity: 0 }, { opacity: 1, offset: 0.01 }, { transform: `scale(${to.width / deckW})`, opacity: vanish ? 0 : 1 }], { ...timing, easing: 'cubic-bezier(.45,.05,.35,1)', fill: 'backwards' });
      flightShadow(el, SHADOW.arc, timing);
      flights.push(f);
      void f.finished.then(() => el.remove(), () => el.remove());
    };
    root.querySelectorAll<HTMLElement>('.ht-hand .ht-card').forEach((el, i) => {
      const to = el.getBoundingClientRect(), b = centre(to);
      const timing = { duration: DEAL_FLIGHT_MS, delay: at(0, i) * DEAL_STEP_MS };
      flights.push(el.animate([{ transform: `translate(${from.x - b.x}px, ${from.y - b.y}px) scale(${deckW / to.width})`, opacity: 0 }, { opacity: 1, offset: 0.01 }, { transform: 'none', opacity: 1 }], { ...timing, easing: 'cubic-bezier(.45,.05,.35,1)', fill: 'backwards' }));
      flightShadow(el, SHADOW.arc, timing);
    });
    for (const seat of [1, 2]) {
      const backs = [...root.querySelectorAll<HTMLElement>(`.ht-backs[data-src="${seat}"] .ht-back`)];
      if (backs.length) {
        backs.forEach((el, i) => {
          throwTo(el.getBoundingClientRect(), at(seat, i), false);
          flights.push(el.animate([{ opacity: 0 }, { opacity: 0 }], { duration: at(seat, i) * DEAL_STEP_MS + DEAL_FLIGHT_MS }));
        });
        continue;
      }
      const tile = seatBox(seat);
      if (tile) for (let i = 0; i < 7; i++) throwTo(box(tile, 40), at(seat, i), true);
    }
    root.querySelectorAll<HTMLElement>('.t58-musik__card').forEach((el, i) => {
      throwTo(el.getBoundingClientRect(), at('m', i), false);
      flights.push(el.animate([{ opacity: 0 }, { opacity: 0 }], { duration: at('m', i) * DEAL_STEP_MS + DEAL_FLIGHT_MS }));
    });
    root.dataset.dealing = '';
    let over = false;
    const end = () => {
      if (over) return;
      over = true;
      delete root.dataset.dealing;
    };
    const cancel = whenLanded(flights, 23 * DEAL_STEP_MS + DEAL_FLIGHT_MS + 1000, end);
    return () => {
      cancel();
      end();
      for (const el of ghosts) el.remove();
    };
  }, [deal]); // eslint-disable-line react-hooks/exhaustive-deps -- one per deal
}

/** A copy flying from `from` to `to` above the table (shrinking to `to`'s size). */
function fly(from: DOMRect, to: DOMRect, pic: ReturnType<typeof backPic>, delay: number, duration: number, vanish: boolean, ghosts: HTMLElement[]) {
  const w = Math.max(from.width, to.width);
  const a = centre(from), b = centre(to);
  const el = ghost(box(from, w), pic, ghosts);
  el.style.opacity = '0';
  const timing = { duration, delay };
  const f = el.animate([{ transform: `scale(${from.width / w})`, opacity: 1 }, { transform: `translate(${b.x - a.x}px, ${b.y - a.y}px) scale(${to.width / w})`, opacity: vanish ? 0 : 1 }], { ...timing, easing: 'cubic-bezier(.3,.6,.3,1)', fill: 'forwards' });
  flightShadow(el, SHADOW.arc, timing);
  void f.finished.then(() => el.remove(), () => el.remove());
}

/** A card of the player's hand that has just come: it flies in from `from` (FLIP), turning face up. */
function flyIn(el: HTMLElement, from: DOMRect, delay: number, duration: number) {
  const to = el.getBoundingClientRect(), a = centre(from), b = centre(to);
  const timing = { duration, delay };
  el.animate([{ transform: `translate(${a.x - b.x}px, ${a.y - b.y}px) scale(${from.width / to.width})`, opacity: 0 }, { opacity: 1, offset: 0.01 }, { transform: 'none', opacity: 1 }], { ...timing, easing: 'cubic-bezier(.3,.6,.3,1)', fill: 'backwards' });
  flightShadow(el, SHADOW.arc, timing);
}

/** A card the declarer gave: to its taker (face up where the player sees it). */
function useGiftFlights(gift: number, st: TableState, deck: string, back: string): void {
  useLayoutEffect(() => {
    const done = st.lastGift;
    const root = document.querySelector<HTMLElement>('.t1k');
    if (!gift || !done || !root || typeof root.animate !== 'function') return;
    const ghosts: HTMLElement[] = [];
    const small = cardW() * 0.6;
    const fromBox = done.from === 0 ? handRects.get(cardKey(done.card)) ?? seatBox(0) : seatBox(done.from);
    if (!fromBox) return;
    const inHand = done.to === 0 ? root.querySelector<HTMLElement>(`.ht-hand .ht-card[data-card="${cardKey(done.card)}"]`) : null;
    if (inHand) flyIn(inHand, box(fromBox, small), 0, GIVE_FLY_MS);
    else {
      const toBox = seatBox(done.to);
      if (toBox) fly(done.from === 0 ? fromBox : box(fromBox, small), box(toBox, small), done.from === 0 ? facePic(deck, done.card) : backPic(back), 0, GIVE_FLY_MS, true, ghosts);
    }
    return () => {
      for (const el of ghosts) el.remove();
    };
  }, [gift]); // eslint-disable-line react-hooks/exhaustive-deps -- one per card given
}

/** The musik flies to the declarer. */
function useMusikFlights(g: State1000 | null, deck: string): void {
  const seen = useRef<{ musik: number; deal: number } | null>(null);
  useLayoutEffect(() => {
    if (!g) return;
    const before = seen.current;
    seen.current = { musik: g.musik.length, deal: g.handNo };
    const root = document.querySelector<HTMLElement>('.t1k');
    if (!before || before.deal !== g.handNo || !root || typeof root.animate !== 'function') return;
    if (!before.musik || g.musik.length || g.phase !== 'give') return;
    const ghosts: HTMLElement[] = [];
    const rects = musikRects.slice();
    g.musikCards.forEach((c, i) => {
      const from = rects[i] ?? musikBox();
      if (!from) return;
      const el = g.declarer === 0 ? root.querySelector<HTMLElement>(`.ht-hand .ht-card[data-card="${cardKey(c)}"]`) : null;
      if (el) return flyIn(el, from, i * 90, MUSIK_FLY_MS);
      const to = seatBox(g.declarer);
      if (to) fly(from, box(to, from.width * 0.7), facePic(deck, c), i * 90, MUSIK_FLY_MS, true, ghosts);
    });
  });
}

/** The full trick slides to its taker (the store's `collecting`). */
function useCollect(g: State1000 | null, collecting: boolean) {
  useLayoutEffect(() => {
    if (!collecting || !g || g.winner < 0) return;
    const to = g.winner === 0 ? (document.querySelector('.t1k .ht-hand')?.getBoundingClientRect() ?? seatBox(0)) : seatBox(g.winner);
    if (!to) return;
    const b = centre(to);
    for (const el of document.querySelectorAll<HTMLElement>('.t1k .ht-trick__fly')) {
      if (typeof el.animate !== 'function') continue;
      const a = centre(el.getBoundingClientRect());
      el.animate([{ transform: 'none', opacity: 1 }, { transform: `translate(${b.x - a.x}px, ${b.y - a.y}px) scale(0.45)`, opacity: 0 }], { duration: COLLECT_MS, easing: 'ease-in', fill: 'forwards' });
    }
  }, [collecting]); // eslint-disable-line react-hooks/exhaustive-deps -- one per trick
}

export function Table1000() {
  const st = use1000();
  const g = st.game;
  const skin = useUiState().options.skin;
  const phone = usePhone();
  useDealAnimation(st.deal, g, skin.cardBackType);
  useGiftFlights(st.gift, st, skin.deckType, skin.cardBackType);
  useMusikFlights(g, skin.deckType);
  useCollect(g, st.collecting);
  return (
    <div id="main" className="board ht t58 t1k" data-board="" data-game="thousand" data-layout={phone ? 'compact' : 'classic'} data-phase={g?.phase} data-deck={skin.deckType}>
      {g ? phone ? <CompactTable g={g} deck={skin.deckType} back={skin.cardBackType} /> : <ClassicTable g={g} deck={skin.deckType} back={skin.cardBackType} /> : null}
    </div>
  );
}

/** The hero of the score window: the marriage of hearts with the ace and the ten. */
const HERO = ['10h', 'Kh', 'Ah', 'Qh', '9h'];
const FIT_STEPS = 6;

/** The end of a deal or of the game: the contract and whether it was made, each seat's points of the deal (cards and
 *  marriages), the totals with a crown on the highest. */
export function ScoreWindow1000({ instance }: { instance: WindowInstance }) {
  const st = use1000();
  const g = st.game;
  const skin = useUiState().options.skin;
  useFitWindow('.lbx_window.lbx_t1k_score', FIT_STEPS, g ? `${g.handNo}-${g.phase}` : null);
  const newGame = useNewGame();
  const online = !!useMultiplayerState().cards;
  if (!g) return null;
  const over = g.phase === 'over';
  const best = leaders(g.scores);
  const youWin = playerWon(g);
  const last = g.history[g.history.length - 1];
  const go = () => {
    if (over) return newGame();
    uiStore().closeWindow('t1k_score');
    store1000().nextHand();
  };
  const title = over ? (youWin ? t('hearts.score.youWinMost') : t('hearts.score.game')) : t('t1k.score.deal', { n: g.handNo + 1 });
  const sub = !last ? '' : t(last.made ? 't1k.score.made' : 't1k.score.failed', { name: seatName(last.declarer), n: last.contract });
  const detail = (p: number): string => {
    if (!last) return '';
    const got = last.cards[p]! + last.melds[p]!;
    if (p === last.declarer) return t('t1k.takenOf', { n: got, of: last.contract });
    if (last.earned[p]! > 0 && last.points[p] === 0) return t('t1k.barrel');
    return last.melds[p] ? t('t1k.score.withMeld', { n: last.cards[p]!, m: last.melds[p]! }) : t('t1k.taken', { n: got });
  };
  return (
    <WindowFrame name="t1k_score" header={t(over ? 'hearts.score.game' : 'hearts.score.hand')} overlay closing={instance.closing}>
      <div className="tab_contents">
        <div className="hs-backdrop" aria-hidden="true" style={{ '--hs-back': `url("${backSrc(skin.cardBackType)}")` } as CSSProperties} />
        <div className="hs-cont">
          <div className="hs-hero" aria-hidden="true">
            {HERO.map((id, i) => (
              <img key={id} className="hs-hero__card" src={faceSrc(skin.deckType, id)} srcSet={faceSrcSet(skin.deckType, id)} alt="" draggable={false} style={{ '--i': i - 2 } as CSSProperties} />
            ))}
          </div>
          <div className="hs-title" data-win={youWin || undefined}>
            {title}
          </div>
          <div className="hs-sub">{flaggedText(over && !youWin ? t('hearts.score.winnerMost', { name: best.map(seatName).join(', ') }) : sub)}</div>
          <div className="hs-tiles t58-tiles">
            {[0, 1, 2].map((p) => {
              const pts = last?.points[p] ?? 0;
              return (
                <div key={p} className="hs-tile" data-me={p === 0 || undefined} data-best={best.includes(p) || undefined}>
                  <SayBubble seat={p} />
                  <div className="hs-tile__who">
                    <SeatAvatar name={seatName(p)} flag={seatFlag(p)} />
                    <b>{seatName(p)}</b>
                    {best.includes(p) ? <CrownIcon /> : null}
                  </div>
                  <div className="hs-tile__deal" data-zero={pts === 0 || undefined} data-minus={pts < 0 || undefined}>
                    {pts > 0 ? `+${pts}` : String(pts)}
                  </div>
                  <div className="hs-tile__total">{g.scores[p]}</div>
                  <small>{detail(p)}</small>
                </div>
              );
            })}
          </div>
          {/* online no Ranking score: the game's own target (the state's) */}
          <p className="hs-legend">{over && youWin && !online ? t('t58.score.win', { n: winScore(g) }) : t('t1k.score.legend', { n: g.target })}</p>
          {online ? (over ? <QuickSay iWon={youWin} /> : <QuickSay key={g.handNo} iWon={false} deal />) : null}
        </div>
        <AdSlot slot={2} />
        {online ? (
          // online: the next deal comes by itself; after the game, the same table again or leave it
          <div className="rules_buttons hs-buttons">
            {over ? (
              <>
                <input type="button" className="mp_btn_secondary" id="mp_cards_leave" value={t('lobby.room.leave')} onClick={() => mpStore().leaveCards()} />
                <input
                  type="button"
                  className="nomoves_shuffle"
                  id="mp_cards_again"
                  value={t('lobby.again')}
                  onClick={() => {
                    // mpStore().again() closes Makao's and Kierki's end windows only
                    uiStore().closeWindow('t1k_score');
                    mpStore().again();
                  }}
                />
              </>
            ) : (
              <NextDealNote />
            )}
          </div>
        ) : (
          <div className="rules_buttons hs-buttons">
            <input type="button" className="nomoves_shuffle" id="t1k_next" value={t(over ? 'hearts.score.newGame' : 'hearts.score.next')} onClick={go} />
          </div>
        )}
      </div>
    </WindowFrame>
  );
}

